// Helper function for Server-Sent Events (SSE) with authentication
export interface SSEOptions {
    url: string;
    onMessage: (data: any) => void;
    onError?: (error: any) => void;
    onComplete?: () => void;
    signal?: AbortSignal;
}

export const createSSEConnection = ({ url, onMessage, onError, onComplete, signal }: SSEOptions) => {
    let reader: ReadableStreamDefaultReader<Uint8Array> | null = null;
    let isCompleted = false;
    let aborted = false;

    const connect = async () => {
        // Get auth token from localStorage
        const user = localStorage.getItem('user');
        let token = '';

        if (user) {
            try {
                const userData = JSON.parse(user);
                token = userData?.tokens?.access || '';
            } catch (error) {
                console.error('Error parsing user data:', error);
                if (onError) {
                    onError(error);
                }
                return;
            }
        }

        // Construct URL with base URL if needed
        let fullUrl = url;
        const host = process.env.REACT_APP_API_HOST_DEV;
        const baseURL = process.env.REACT_APP_API_BASE_URL;

        if (window.location.host === host && baseURL) {
            fullUrl = baseURL + url;
        }

        try {
            // Use fetch with streaming instead of EventSource to support custom headers
            const response = await fetch(fullUrl, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
                signal,
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            if (!response.body) {
                throw new Error('Response body is null');
            }

            reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';

            while (!aborted && !isCompleted) {
                const { done, value } = await reader.read();

                if (done) {
                    break;
                }

                // Decode the chunk and add to buffer
                buffer += decoder.decode(value, { stream: true });

                // Process complete SSE messages
                const lines = buffer.split('\n');
                buffer = lines.pop() || ''; // Keep incomplete line in buffer

                for (const line of lines) {
                    if (line.startsWith('data: ')) {
                        const dataStr = line.slice(6); // Remove 'data: ' prefix

                        try {
                            const data = JSON.parse(dataStr);

                            // Check if this is the complete event
                            if (data.status === 'complete') {
                                isCompleted = true;
                                if (onComplete) {
                                    onComplete();
                                }
                                cleanup();
                                return;
                            }

                            // Pass data to the message handler
                            onMessage(data);
                        } catch (parseError) {
                            console.error('Error parsing SSE data:', parseError, dataStr);
                            if (onError) {
                                onError(parseError);
                            }
                        }
                    }
                }
            }

            cleanup();
        } catch (error: any) {
            if (error.name !== 'AbortError' && !aborted) {
                console.error('SSE connection error:', error);
                if (onError && !isCompleted) {
                    onError(error);
                }
            }
            cleanup();
        }
    };

    const cleanup = () => {
        aborted = true;
        if (reader) {
            reader.cancel().catch(() => {});
            reader = null;
        }
    };

    // Handle abort signal
    if (signal) {
        signal.addEventListener('abort', () => {
            cleanup();
        });
    }

    // Start the connection
    connect();

    // Return cleanup function
    return cleanup;
};
